import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../../api/client';
import { useNotify } from '../../../lib/notify';
import {
  ACCOUNT_TYPES,
  accountDefaults,
  accountFormSchema,
  accountPatch,
  ROLE_FOR_TYPE,
  roleOf,
  type Account,
  type AccountFormValues,
} from '../lib/accounts';
import { applyApiErrors, bodyField } from '../lib/formErrors';

const toField = bodyField([
  'code',
  'name',
  'type',
  'role',
  'isPaymentAccount',
  'isActive',
] as const);

/** Add an account, or rename/recode/deactivate one (type is fixed after creation). */
export function AccountDialog({
  open,
  account,
  onClose,
}: {
  open: boolean;
  account: Account | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const editing = Boolean(account);
  const isSystem = Boolean(account?.systemKey);
  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    values: accountDefaults(account),
  });
  const { control, handleSubmit, setError, formState } = form;
  const roleOption = ROLE_FOR_TYPE[useWatch({ control, name: 'type' })];
  // One key per new account, so a double submit or retry cannot create it twice.
  const idemKey = useRef(crypto.randomUUID());

  const save = useMutation({
    mutationFn: (v: AccountFormValues) =>
      account
        ? unwrap(
            api.PATCH('/accounts/{id}', {
              params: { path: { id: account.id } },
              body: accountPatch(account, v),
            }),
          )
        : unwrap(
            api.POST('/accounts', {
              params: { header: { 'idempotency-key': idemKey.current } },
              body: {
                code: v.code.trim(),
                name: v.name.trim(),
                type: v.type,
                role: roleOf(v),
                isPaymentAccount: v.isPaymentAccount,
              },
            }),
          ),
    onSuccess: (a) => {
      idemKey.current = crypto.randomUUID();
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
      notify.success(editing ? `Saved ${a.name}` : `Added ${a.name}`);
      onClose();
    },
    onError: (e) => {
      if (!applyApiErrors(e, setError, toField)) notify.error(e);
    },
  });

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => void handleSubmit((v) => save.mutate(v))(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit account' : 'Add account'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {isSystem && (
              <Alert severity="info">
                This is a system account the books rely on. You can rename it, but not deactivate
                it.
              </Alert>
            )}
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  disabled={editing}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    ACCOUNT_TYPES.find((t) => t.value === field.value)?.hint
                  }
                >
                  {ACCOUNT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Stack direction="row" spacing={1.5}>
              <Controller
                name="code"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Code"
                    sx={{ width: 130 }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ?? 'e.g. 1010'}
                  />
                )}
              />
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Name"
                    fullWidth
                    autoFocus={!editing}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ?? 'e.g. Meezan Bank'}
                  />
                )}
              />
            </Stack>
            {roleOption && (
              <Controller
                name="role"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    label="Product categories"
                    disabled={isSystem}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      (isSystem
                        ? 'Products without their own account post here.'
                        : 'Lets a product category post to this account. Fixed once the account has postings.')
                    }
                  >
                    <MenuItem value="">Not for categories</MenuItem>
                    <MenuItem value={roleOption.value}>{roleOption.label}</MenuItem>
                  </TextField>
                )}
              />
            )}
            <Controller
              name="isPaymentAccount"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Money can be received into or paid from this account"
                />
              )}
            />
            {editing && !isSystem && (
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={save.isPending}
            disabled={editing && !formState.isDirty}
          >
            {editing ? 'Save' : 'Add account'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
